import { CommonModule, DatePipe } from '@angular/common';
import { Component, NO_ERRORS_SCHEMA, ChangeDetectorRef, inject, AfterViewInit, OnDestroy, OnInit, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { DocumentsService } from '@k12/documents';
import { AppInsightsService } from '@k12/error_handler';
import { LoadingSpinnerService } from '@k12/loading';
import { DocumentsList, ProviderInfoStatus, SeverityLevel, ToastMessageType, VersionDocument } from '@k12/models';
import { ToastMessageService } from '@k12/notifications';
import { SystemService } from '@k12/system';
import { CfiMatTableComponent, ColumnDefinition } from '@k12/tables';
import { StatusBadgeComponent } from '@k12/ui-primitives';
import { Subject, takeUntil } from 'rxjs';

enum ColumnWidths {
  action = 17,
  lastModified = 15,
  fileName = 25,
  status = 10,
  version = 11,
  documentType = 20,
}

enum ExpandedColumnWidths {
  action = 13.5,
  lastModified = 15.1,
  fileName = 25,
  status = 13,
  documentType = 20,
  version = 11.5
}

enum ExpandedColumnWidthsWithoutAction {
  action = 0,
  lastModified = 18.1,
  fileName = 25,
  status = 13,
  documentType = 20,
  version = 18.5
}

export enum TableSelection {
  complete = 'complete',
  toDo = 'toDo'
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-shared-documents',
  imports: [
    CfiMatTableComponent,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSelectModule,
    MatTableModule,
    StatusBadgeComponent
  ],
  providers: [DatePipe],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.scss',
  schemas: [NO_ERRORS_SCHEMA]
})
export class DocumentsComponent implements AfterViewInit, OnDestroy, OnInit {
  public accountId = input<string>();
  public columnDefinitions: ColumnDefinition[] = [
    {
      name: 'documentType',
      header: 'Document Type',
      sortable: true,
      width: ColumnWidths.documentType,
      expandedWidth: ExpandedColumnWidths.documentType,
      expandable: true,
      toggleExpand: true
    },
    {
      name: 'fileName',
      header: 'Document Name',
      sortable: true,
      width: ColumnWidths.fileName,
      expandedWidth: ExpandedColumnWidths.fileName,
      expandable: true
    },
    {
      name: 'version',
      header: 'Version',
      sortable: true,
      width: ColumnWidths.version,
      expandedWidth: ExpandedColumnWidths.version,
      expandable: true
    },
    {
      name: 'lastModified',
      header: 'Date/ Timestamp',
      sortable: true,
      width: ColumnWidths.lastModified,
      expandedWidth: ExpandedColumnWidths.lastModified,
      expandable: true
    },
    {
      name: 'status',
      header: 'Status',
      sortable: true,
      width: ColumnWidths.status,
      expandedWidth: ExpandedColumnWidths.status,
      expandable: true
    },
    {
      name: 'action',
      header: 'Action',
      sortable: false,
      action: true,
      width: ColumnWidths.action,
      expandedWidth: ExpandedColumnWidths.action,
      expandable: true
    }
  ];
  public container = input.required<string>();
  public displayedColumns: string[] = []; // Required for table component
  public documentTableData = signal<DocumentsList[]>([]);
  public entityId = input<string>();
  public expandedElement: DocumentsList | null = null; // Required for table component
  public loading = signal(true);
  ProviderInfoStatus: typeof ProviderInfoStatus = ProviderInfoStatus;
  public showActionColumn = input<boolean>(true);
  public status = input<ProviderInfoStatus | undefined>(undefined);

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly documents: DocumentsList[] = [];
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly documentsService = inject(DocumentsService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly systemService = inject(SystemService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly unsubscribe = new Subject<void>();

  public getDocumentsLength(): number {
    return this.documentTableData().reduce((sum, current) => sum + (current.versions?.length ?? 0) + 1, 0);
  }

  public ngAfterViewInit(): void {
    this.documentsService
      .getDocumentList(this.container(), this.entityId(), this.accountId())
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (res: DocumentsList[]) => {
          for (const file of res) {
            this.documents.push(file);
          }
          this.documentTableData.set([...this.documents]);
          this.documentTableData().forEach((document) => {
            document.versions.forEach((ver: VersionDocument) => {
              ver.lastModified = this.systemService.isoDateToFormatted(ver.lastModified);
              ver.fileName = document.metadata.fileName;
            });
            document['subCells'] = document.versions;
            document['fileName'] = document.metadata.fileName;
            document.documentType = document.metadata.documentType;
          });

          // Sort documents by last modified date, with the most recent first
          const sortedDocuments = [...this.documentTableData()].sort((a, b) => {
            const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
            const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
            return dateB - dateA;
          });
          this.documentTableData.set(sortedDocuments);
          console.log('Fetched documents:', this.documentTableData());
          this.loadingSpinnerService.hideSpinner();
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: (error: { error?: { message?: string } }) => {
          this.loadingSpinnerService.hideSpinner();
          this.loading.set(false);
          const errorMessage = error?.error?.message || 'An error occurred while fetching documents.';
          this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
          this.toastMessageService.showToast(ToastMessageType.Error, 'You are not properly setup for documents.');
          this.cdr.markForCheck();
        }
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public ngOnInit(): void {
    this.displayedColumns = this.setColumns(this.columnDefinitions);
  }

  public onExpandedElementChange(element: DocumentsList | null): void {
    this.expandedElement = element;

    // Update the expanded property on the task
    if (element) {
      element.expanded = true;
    } else {
      // Reset expanded state on all tasks
      this.documents.forEach((document) => {
        document.expanded = false;
      });
    }
    this.cdr.markForCheck();
  }

  public downloadDocument(document: DocumentsList | VersionDocument): void {
    const containerName = this.container();
    const blobPath = document.downloadPath;

    this.documentsService.downloadFile(containerName, blobPath).catch((error: Error) => {
      this.appInsightsService.log(error, SeverityLevel.error);
      this.toastMessageService.showToast(ToastMessageType.Error, 'Failed to download file. Please try again.');
    });
  }

  private setColumns(colDefs: ColumnDefinition[]): string[] {
    if (this.showActionColumn()) {
      return colDefs.map((col) => col.name);
    } else {
      return colDefs.filter((col) => col.name !== 'action').map((col) => col.name);
    }
  }

  getExandedColumnWidth(columnName: string): number {
    const column = this.columnDefinitions.find((col) => col.name === columnName);
    if (!column) {
      return 0; // or some default width
    }
    return this.showActionColumn()
      ? (column.expandedWidth ?? 0)
      : (ExpandedColumnWidthsWithoutAction[columnName as keyof typeof ExpandedColumnWidthsWithoutAction] ?? 0);
  }
}
